/**
 * Public alumni directory — open URL `/alumni-directory`.
 * No auth needed. Returns only opted-in alumni and non-PII fields.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { GraduationCap, MagnifyingGlass, LinkedinLogo, MapPin, Briefcase, HandHeart } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") || "";

export default function PublicAlumniDirectory() {
  const [data, setData] = useState({ by_year: [], count: 0 });
  const [q, setQ] = useState("");
  const [year, setYear] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async (query = "", yr = "") => {
    setLoading(true);
    try {
      const params = {};
      if (query) params.q = query;
      if (yr) params.year = yr;
      const r = await axios.get(`${BACKEND_URL}/api/public/alumni-directory`, { params });
      setData(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-[var(--vs-bg)]">
      <header className="bg-gradient-to-r from-[var(--vs-primary)] to-[var(--vs-secondary)] text-white py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <Link to="/" className="text-xs font-mono uppercase tracking-widest opacity-80 hover:opacity-100">← Back to Vidya Seva</Link>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold mt-3 flex items-center gap-3">
            <GraduationCap weight="duotone" size={48} />
            Alumni directory
          </h1>
          <p className="text-sm sm:text-base mt-3 max-w-2xl opacity-90">
            Where our students are today. Reach out for mentorship or hire someone who's already lived the journey.
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex flex-col sm:flex-row gap-3 mb-6 items-stretch sm:items-center">
          <div className="flex-1 relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--vs-text-secondary)]" />
            <Input
              data-testid="pubdir-search"
              placeholder="Search name, company, city, role…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load(q, year)}
              className="pl-9 bg-white"
            />
          </div>
          <Input
            data-testid="pubdir-year"
            type="number"
            min="2000"
            max="2099"
            placeholder="Filter by year"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(q, year)}
            className="bg-white max-w-[160px] font-mono"
          />
          <Button data-testid="pubdir-search-btn" onClick={() => load(q, year)} disabled={loading}>{loading ? "…" : "Search"}</Button>
          <Button asChild variant="outline" className="bg-white"><a href="/" data-testid="pubdir-home">Home</a></Button>
        </div>

        {!loading && data.count === 0 && (
          <div className="text-center py-16 text-[var(--vs-text-secondary)]" data-testid="pubdir-empty">
            <GraduationCap size={48} weight="thin" className="mx-auto mb-3 opacity-50" />
            No alumni match this filter — or none have opted-in to the public directory yet.
          </div>
        )}

        <div className="space-y-8">
          {data.by_year.map((g) => (
            <section key={g.year} data-testid={`pubdir-year-${g.year}`}>
              <div className="flex items-baseline gap-3 mb-3">
                <div className="text-3xl font-bold font-mono">{g.year || "Year unknown"}</div>
                <div className="text-xs text-[var(--vs-text-secondary)]">{g.members.length} alumni</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {g.members.map((m) => (
                  <article key={m.id} className="vs-card p-5 hover:shadow-md transition-shadow" data-testid={`pubdir-card-${m.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="font-medium">{m.name}</div>
                      {m.willing_to_mentor && (
                        <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50 text-[10px]"><HandHeart size={10} className="mr-1" /> Mentor</Badge>
                      )}
                    </div>
                    {(m.current_company || m.current_role) && (
                      <div className="text-sm text-[var(--vs-text-secondary)] flex items-center gap-1.5"><Briefcase size={12} className="opacity-60" /> {[m.current_role, m.current_company].filter(Boolean).join(" · ")}</div>
                    )}
                    {m.current_city && (
                      <div className="text-sm text-[var(--vs-text-secondary)] flex items-center gap-1.5"><MapPin size={12} className="opacity-60" /> {m.current_city}</div>
                    )}
                    {m.bio && <p className="text-xs text-[var(--vs-text-secondary)] mt-2 italic">"{m.bio}"</p>}
                    {m.linkedin_url && (
                      <a href={m.linkedin_url} target="_blank" rel="noopener noreferrer"
                         className="text-[var(--vs-secondary)] text-sm mt-2 inline-flex items-center gap-1.5 underline decoration-dotted"
                         data-testid={`pubdir-li-${m.id}`}><LinkedinLogo size={14} /> LinkedIn</a>
                    )}
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-12 text-center text-xs text-[var(--vs-text-secondary)] border-t pt-6">
          Are you an alumnus and don't see your card?{" "}
          <a href="/login" className="text-[var(--vs-secondary)] underline decoration-dotted">Sign in to your account</a>{" "}
          and ask an admin to enable your public profile.
        </div>
      </main>
    </div>
  );
}
